import React, { useEffect, useState, useRef } from "react";
import * as pdfjsLib from "pdfjs-dist";

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/**
 * TikzRenderer - Renders TikZ/PGFPlots diagrams via backend proxy
 *
 * Backend uses:
 * - LaTeX Online (latexonline.cc) for PGFPlots - returns PDF
 * - node-tikzjax for basic TikZ - returns SVG
 *
 * Supports:
 * - TikZ drawings (\begin{tikzpicture}...\end{tikzpicture})
 * - PGFPlots charts (\begin{axis}...\end{axis})
 * - Mathematical graphs and diagrams
 */

interface Props {
  code: string;
}

const baseUrl = process.env.REACT_APP_API_CONFIG;

export default function TikzRenderer({ code }: Props) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let mounted = true;

    const renderTikz = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Call our backend proxy to render TikZ
        const response = await fetch(`${baseUrl}/tikz/render`, {
          method: "POST",
          body: JSON.stringify({ code }),
          headers: {
            "Content-Type": "application/json",
          },
        });

        const result = await response.json();

        if (!mounted) return;

        if (result.success) {
          const { data } = result;

          if (data.format === "pdf" && data.pdfDataUrl) {
            // Render PDF to canvas using pdf.js
            try {
              const pdfData = atob(data.pdfDataUrl.split(",")[1]);
              const pdfArray = new Uint8Array(pdfData.length);
              for (let i = 0; i < pdfData.length; i++) {
                pdfArray[i] = pdfData.charCodeAt(i);
              }

              const pdf = await pdfjsLib.getDocument({ data: pdfArray }).promise;
              const page = await pdf.getPage(1);

              // Render at 2x scale for better quality
              const scale = 2;
              const viewport = page.getViewport({ scale });

              if (canvasRef.current) {
                const canvas = canvasRef.current;
                const context = canvas.getContext("2d");
                if (context) {
                  canvas.height = viewport.height;
                  canvas.width = viewport.width;

                  await page.render({
                    canvasContext: context,
                    viewport: viewport,
                    canvas: canvas,
                  }).promise;

                  // Convert canvas to image URL
                  const imgUrl = canvas.toDataURL("image/png");
                  setImageUrl(imgUrl);
                }
              }
            } catch (pdfError) {
              console.error("PDF render error:", pdfError);
              setError("Failed to render PDF diagram");
            }
          } else {
            // SVG format - use directly
            setImageUrl(data.svgDataUrl || data.svgUrl || data.imageUrl);
          }
          setIsLoading(false);
        } else {
          setError(result.error || "Rendering failed");
          setIsLoading(false);
        }
      } catch (err) {
        if (mounted) {
          console.error("TikZ render error:", err);
          setError(String(err));
          setIsLoading(false);
        }
      }
    };

    renderTikz();

    return () => {
      mounted = false;
    };
  }, [code]);

  if (error) {
    // Show formatted code block as fallback when rendering fails
    let displayCode = code.trim();
    displayCode = displayCode.replace(/^```\w*\s*/, "");
    displayCode = displayCode.replace(/\s*```$/, "");

    return (
      <div className="my-4">
        <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-t-lg px-4 py-2 flex items-center gap-2">
          <span className="text-yellow-400">📊</span>
          <span className="text-yellow-400 text-sm font-medium">TikZ/PGFPlots Diagram (Code Preview)</span>
        </div>
        <pre className="bg-[#1e1e1e] rounded-b-lg p-4 overflow-x-auto text-sm text-gray-300 font-mono whitespace-pre-wrap">
          {displayCode}
        </pre>
      </div>
    );
  }

  return (
    <div className="tikz-container my-4 relative rounded-lg overflow-hidden bg-[#2d2d2d] min-h-[200px]">
      {/* Hidden canvas for PDF rendering */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#2d2d2d] z-10">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-white/20 border-t-white/80"></div>
            <span className="text-white/60 text-sm">Rendering diagram...</span>
          </div>
        </div>
      )}
      {imageUrl && (
        <div className="flex justify-center p-4 bg-white rounded-lg">
          <img
            src={imageUrl}
            alt="TikZ Diagram"
            className="max-w-full h-auto"
            onError={() => {
              setError("Failed to load rendered diagram");
            }}
          />
        </div>
      )}
    </div>
  );
}
